import {Component} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NgSelectModule} from "@ng-select/ng-select";
import {NgxDatatableModule} from "@swimlane/ngx-datatable";
import {ApiService} from "@ctms/api.service";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {OrderType, Pagination, PaginationResponse} from "@models/pagination.model";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {NgbProgressbar} from "@ng-bootstrap/ng-bootstrap";
import {PatientStatisticsService} from "@shared/api/services/v2/patient-statistics.service";
import {Caseload} from "@shared/api/models/query/patient-statistics.model";
import {PatientBasic} from "@shared/api/models/query/patient.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {DashboardV2FilterComponent} from "@cdss/pages/dashboard-v2/filter/dashboard-v2-filter.component";
import {PatientFilter} from "@cdss/pages/dashboard-v2/filter/filter.dto";
import {OverlaySpinnerComponent} from "@shared/components/overlay-spinner/overlay-spinner.component";
import {RouterLink} from "@angular/router";
import {AuthService} from "@shared/services/security/auth.service";
import {User} from "@models/user.model";
import {TimeAgoPipe} from "@shared/pipes/time-ago.pipe";
import {MaskedFieldComponent} from "@shared/components/masked-field/masked-field.component";

@Component({
    selector: 'cdss-dashboard-v2',
    imports: [CommonModule, ReactiveFormsModule, SpinnerComponent, NgSelectModule, NgxDatatableModule, NgbProgressbar, DashboardV2FilterComponent, FormsModule, OverlaySpinnerComponent, RouterLink, TimeAgoPipe, MaskedFieldComponent],
    providers: [ApiService],
    templateUrl: './dashboard-v2.page.html',
    styleUrl: './dashboard-v2.page.scss'
})
export class DashboardV2Page {
    loading = false
    refreshing = false

    loggedUser: User = null
    pagination: Pagination = new Pagination(25);

    rows: PatientBasic[] = []

    caseload: Caseload = null

    isSidebarOpen = false;

    filter: PatientFilter = {
        scope: "all",
        adherence: [],
        meedRiskStatus: [],
        programStage: [],
        promsTrend: [],
        search: ""
    }

    constructor(
        private breadcrumbService: BreadcrumbService,
        private patientStatisticsService: PatientStatisticsService,
        private patientsService: PatientsService,
        private authService: AuthService
    ) {
    }

    ngOnInit() {
        this.breadcrumbService.set([
            {title: "Patient's Caseload", url: null},
        ])
        this.authService.getLogginedUser().subscribe(user => {
            this.loggedUser = user
        })

        this.getCaseload()
        this.refreshRows()
    }

    getLastActive(patient: PatientBasic): null | string {
        let date1: string | null = null;
        let date2: string | null = null;
        if (patient.user.phoneNumber == null) {
            date1 = patient.carer.user.lastLoginAt
            date2 = patient.carer.user.lastCheckinCompletedAt
        } else {
            date1 = patient.user.lastLoginAt
            date2 = patient.user.lastCheckinCompletedAt
        }

        if (!date1 && !date2) {
            return null;
        }

        if (!date1) {
            return date2;
        }

        if (!date2) {
            return date1;
        }

        const d1 = new Date(date1);
        const d2 = new Date(date2);

        return d1 > d2 ? date1 : date2;
    }

    openSidebar() {
        this.isSidebarOpen = true
    }

    closeSidebar() {
        this.isSidebarOpen = false
    }

    async getCaseload() {
        this.loading = true
        this.caseload = await this.patientStatisticsService.getCaseload()
        this.loading = false
    }

    selectLowAdherence() {
        if (this.isLowAdherenceSelected()) {
            this.filter = {...this.filter, adherence: this.filter.adherence.filter(value => value != "low")}
        } else {
            this.filter.adherence.push("low")
            this.filter = {...this.filter, adherence: [...this.filter.adherence]}
        }
        this.pagination.start = 0
        this.refreshRows()
    }

    isLowAdherenceSelected() {
        return this.filter.adherence.includes("low")
    }

    isActivePatientsSelected() {
        return this.filter.scope == "active"
    }

    isHighMeedRiskSelected() {
        return this.filter.meedRiskStatus.includes("High")
    }

    selectHighMeedRisk() {
        if (this.isHighMeedRiskSelected()) {
            this.filter = {...this.filter, meedRiskStatus: this.filter.meedRiskStatus.filter(value => value != "High")}
        } else {
            this.filter.meedRiskStatus.push("High")
            this.filter = {...this.filter, meedRiskStatus: [...this.filter.meedRiskStatus]}
        }
        this.pagination.start = 0
        this.refreshRows()
    }

    selectActivePatients() {
        if (this.isActivePatientsSelected()) {
            this.filter = {...this.filter, scope: "all"}
        } else {
            this.filter = {...this.filter, scope: "active"}
        }
        this.pagination.start = 0
        this.refreshRows()
    }

    applyFilter() {
        this.isSidebarOpen = false
        this.pagination.start = 0
        this.refreshRows()
    }

    setPaginationSize(size: number) {
        this.pagination.size = size
        this.pagination.start = 0
        this.refreshRows()
    }

    previousPage() {
        this.pagination.start = this.pagination.currentPage - 2
        this.refreshRows()
    }

    nextPage() {
        this.pagination.start = this.pagination.currentPage
        this.refreshRows()
    }

    refreshRows() {
        this.refreshing = true
        this.pagination.filter = {
            scope: this.filter.scope,
            adherence: this.filter.adherence.join(','),
            meedRiskStatus: this.filter.meedRiskStatus.join(','),
            programStage: this.filter.programStage.join(','),
            promsTrend: this.filter.promsTrend.join(','),
            search: this.filter.search,
            status: 'onboarding,active,completed'
        }
        this.pagination.sort = {prop: 'createdAt', order: OrderType.DESC}
        this.patientsService.listPaginated(this.pagination).subscribe({
            next: (response: PaginationResponse<PatientBasic>) => {
                if (!response?.data?.length) {
                    this.rows = []
                    this.pagination.totalCount = 0
                    this.pagination.fromRecord = 0
                    this.pagination.toRecord = 0
                    this.pagination.currentPage = 0
                    this.refreshing = false
                    return;
                }
                this.rows = response.data
                this.pagination.totalCount = response.pagination.totalCount
                this.pagination.fromRecord = response.pagination.fromRecord
                this.pagination.toRecord = response.pagination.toRecord
                this.pagination.currentPage = response.pagination.currentPage
                this.pagination.totalPages = response.pagination.totalPages
                this.refreshing = false
            }
        })
    }
}
